/**
 * İmzacının AKTİF KİMLİĞİ: store parçaları (DEV-2603, DEV-3899/3901/3903).
 *
 * Parçalar store/index.ts içinde spread edilir (Vuex 'modules' kullanılmaz): action adları
 * zaten global olmalı (uyap-plugin dongu'su `bekleyenKimligiUygula` ve
 * `botBaglantisiniSurdur`u kök action olarak çağırır) ve AppState tipi tek kalır.
 *
 * Model: tarayıcıdaki eİcraPro'nun setConfig'le sessionStorage'a yazdığı token/tenant/tckn
 * yalnız ÖNERİDİR. Öneri sunucudaki lisansla doğrulanır (GET /api/v1/imzaci/kimlik) ve
 * doğrulanırsa AKTİF KİMLİK olur; axios ve bütün kimlik denetimleri aktif kimliği okur.
 * "Hesap TCKN'si" LİSANS SAHİBİNİN TCKN'sidir (bir lisans altında yalnız sahibinin e-imzası).
 * Aktif kimlik yalnız GÜVENLİ NOKTADA değişir: dongu görev almadan hemen önce
 * `bekleyenKimligiUygula`yı çağırır; başka bir hesaba geçiş ASLA kendiliğinden olmaz
 * (kullanıcı şeritteki 'Geç'e basar).
 */
import {ActionContext} from 'vuex';
import app from '@/main';
import {AppState, BekleyenKimlik} from '@/types';
import {
    AKTIF_KIMLIK_ANAHTARI,
    AktifKimlik,
    aktifKimligeDonustur,
    aktifKimligiDogrula,
    botKimligiYuku,
    hemenUygulanabilirMi,
    kimlikKarari,
    LisansKimligi,
    lisansCevabiniCoz,
    oneriParmakIzi,
    oneriyiOku,
    yedekLisans
} from '@/utils/aktifKimlik';
import {
    eicraproDurumunuBelirle,
    eicraproDurumuMesaji,
    EICRAPRO_LISANS_YOK_MESAJI,
    EICRAPRO_TCKN_EKSIK_MESAJI,
    KimlikRedSebebi
} from '@/utils/eicraproBaglantisi';
import {BOT_BEKLEME_METINLERI, BotBeklemeSebebi} from '@/utils/botBekleme';
import {hesabaAitKartiSec, tcknNormalize} from '@/store/oturumKimligi';
import {isletimSistemiBildirimi, uyariSesi} from '@/plugins/kritikUyari';
import {IslemDurum, PlayerDurum} from '@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes';

type Ctx = ActionContext<AppState, AppState>;

/** Lisans sorgusu hata verdiyse sağlık tiki bu süre dolmadan yeniden denemez. */
export const KIMLIK_SORGUSU_YENIDEN_DENEME_MS = 15000;

/** 'Geç' sonrası süren alt görevin bitmesi beklenirken yoklama aralığı. */
export const GECIS_YOKLAMA_MS = 500;

/**
 * 401 sonrası 'token geçersiz' bayrağı kalkabilsin diye lisans sorgusu bir kez daha denenir: AYNI token
 * tarayıcıdan yeniden gelirse (updateToken, sağlık tiki) onunla; gelmezse (axios 401'de ham token'ı siler,
 * tarayıcı yeniden devretmeyebilir) AKTİF kimliğin kendi token'ıyla sağlık tikinden. Bu en erken deneme
 * zamanıdır (ms): bayrak kurulunca KIMLIK_SORGUSU_YENIDEN_DENEME_MS ileri alınır, sorgu 401/403 verince ya
 * da kurtarma yine 401'e dönünce TOKEN_KURTARMA_ARALIKLARI_MS'te büyür: istek fırtınası yok, gerçekten
 * ölü token gece boyunca en çok 5 dakikada bir sorulur. Kullanıcı eylemi (Bağlan, 'Yeniden bağla')
 * beklemeyi ve büyümeyi sıfırlar.
 */
let tokenKurtarmaSonrakiDeneme = 0;
/** Başarısız (sorgu 401/403, karar 'degisiklik-yok' değil) ve geri dönen (kurtarma sonrası yine 401) denemeler. */
let tokenKurtarmaBasarisizSayisi = 0;
let sonTokenKurtarmasi = 0;

/** Token kurtarma sorgularının arası (ms): 15 sn, 1 dk, 2 dk, 5 dk (tavan). */
export const TOKEN_KURTARMA_ARALIKLARI_MS = [KIMLIK_SORGUSU_YENIDEN_DENEME_MS, 60000, 120000, 300000];

/** Son kurtarmadan bu kadar süre geçtiyse yeni 401 temiz bir başlangıçtır (büyüme sıfırlanır). */
const TOKEN_KURTARMA_KARARLI_MS = 600000;

function tokenKurtarmaAraligi(): number {
    const i = Math.min(tokenKurtarmaBasarisizSayisi, TOKEN_KURTARMA_ARALIKLARI_MS.length - 1);
    return TOKEN_KURTARMA_ARALIKLARI_MS[i];
}

/** Kurtarma denemesi bayrağı kaldırmadı (sorgu 401/403 ya da karar 'degisiklik-yok' değil): sonraki aralık büyür. */
function tokenKurtarmaBasarisiz(): void {
    tokenKurtarmaBasarisizSayisi++;
    tokenKurtarmaSonrakiDeneme = Date.now() + tokenKurtarmaAraligi();
}

export function oturumDeposu(): Storage | null {
    try {
        return window.sessionStorage;
    } catch (e) {
        return null;
    }
}

function tokenGovdesi(token: string): string {
    return token.trim().replace(/^Bearer\s*/i, '');
}

function bekle(ms: number): Promise<void> {
    return new Promise((cozum) => setTimeout(cozum, ms));
}

export function aktifKimlikState() {
    return {
        aktifKimlik: null as AktifKimlik | null,
        kimlikTokenGecersiz: false,
        bekleyenKimlik: null as BekleyenKimlik | null,
        kimlikRedSebebi: null as KimlikRedSebebi | null,
        kimlikReddiBuro: null as string | null,
        kimlikOneriParmakIzi: null as string | null,
        kimlikSorgusuHatasi: null as number | null,
        gecisDurumu: null as 'bekliyor' | null,
        oturumYenilenmeli: false
    };
}

/** Şeridin görünüm modeli (KimlikGecisSeridi.vue yalnız bunu çizer). */
export interface KimlikSeridi {
    tur: 'gecis' | 'bilgi';
    metin: string;
    /** 'Geç' düğmesi gösterilsin. */
    gecButonu: boolean;
    /** 'Geç'e basıldı, geçiş yapılıyor (düğmeler gizli). */
    bekliyor: boolean;
    /** Kapatma düğmesinin etiketi: gecişte 'Yoksay', bilgide 'Tamam'. */
    kapatEtiketi: string;
}

/** Kimlik adı ("Av. Ad Soyad", bilinmiyorsa büro adı). */
function avukatAdSoyad(s: AppState): string {
    const a = s.uyapAvukatBilgileri;
    if (a && a.adi && a.soyadi) {
        return `${a.adi} ${a.soyadi}`;
    }
    return '';
}

export const aktifKimlikGetters = {
    aktifKimlik: (s: AppState) => s.aktifKimlik,
    // Plugin dongu next-task cevabındaki tenant_id'yi bununla karşılaştırır. Aktif kimlik
    // yokken `undefined` DEĞİL null: plugin undefined'ı "getter yok (icrapro-app), denetim
    // yok", null'ı "aktif büro yok, görevi işleme" diye okur.
    aktifTenantId: (s: AppState) => (s.aktifKimlik ? s.aktifKimlik.tenant_id : null),
    // Hesap TCKN'si = lisans sahibinin TCKN'si. Token geçersizleşse de DOLU kalır: 401'den
    // sonra kimlik kapısı sahte "başka hesabın oturumu" diyaloğuna düşmesin.
    hesapTckn: (s: AppState) => (s.aktifKimlik ? s.aktifKimlik.tckn : ''),
    // Red sebebi yalnız aktif kimlik YOKKEN durumu belirler; aktif varken yalnız bilgi şeridi.
    eicraproDurumu: (s: AppState) => eicraproDurumunuBelirle(
        s.aktifKimlik !== null && !s.kimlikTokenGecersiz,
        s.aktifKimlik === null ? s.kimlikRedSebebi : null
    ),
    eicraproDurumMesaji: (s: AppState, g: {eicraproDurumu: 'yok' | 'tckn-eksik' | 'tamam'}) => eicraproDurumuMesaji(
        g.eicraproDurumu,
        s.aktifKimlik === null ? s.kimlikRedSebebi : null
    ),
    // İki ad aynı değerin iki yüzü (tek kaynak eicraproDurumu): plugin dongu kapısı
    // isEicraproBagli'yi, görünümler isTokenExis'i okur.
    isEicraproBagli: (_s: AppState, g: {eicraproDurumu: string}) => g.eicraproDurumu === 'tamam',
    isTokenExis: (_s: AppState, g: {eicraproDurumu: string}) => g.eicraproDurumu === 'tamam',
    tenantBilgileri: (s: AppState) => (s.aktifKimlik ? s.aktifKimlik.tenant : undefined),
    kimlikSeridi: (s: AppState): KimlikSeridi | null => {
        const b = s.bekleyenKimlik;
        const aktif = s.aktifKimlik;
        if (b && !b.yoksayildi && aktif && (b.sebep === 'tckn' || b.sebep === 'tenant')) {
            const yeniBuro = b.kimlik.tenant.title || 'yeni';
            if (s.gecisDurumu === 'bekliyor') {
                return {
                    tur: 'gecis',
                    metin: `Geçiş yapılıyor: süren görev bitince bot duraklatılacak ve ${yeniBuro} hesabına geçilecek.`,
                    gecButonu: false,
                    bekliyor: true,
                    kapatEtiketi: ''
                };
            }
            if (b.sebep === 'tckn') {
                const kim = avukatAdSoyad(s) || aktif.tenant.title || 'mevcut hesap';
                return {
                    tur: 'gecis',
                    metin: `Bot Av. ${kim} adına çalışıyor. ${yeniBuro} hesabına geçmek için ‘Geç’e basın.`,
                    gecButonu: true,
                    bekliyor: false,
                    kapatEtiketi: 'Yoksay'
                };
            }
            const eskiBuro = aktif.tenant.title || 'Mevcut';
            return {
                tur: 'gecis',
                metin: `${eskiBuro} bürosunda çalışılıyor. ${yeniBuro} bürosuna geçiş, bot boşta ve sıra boşken ` +
                    'kendiliğinden yapılır; hemen geçmek için ‘Geç’e basın.',
                gecButonu: true,
                bekliyor: false,
                kapatEtiketi: 'Yoksay'
            };
        }
        if (aktif && s.kimlikReddiBuro !== null && s.kimlikRedSebebi !== null) {
            const buro = s.kimlikReddiBuro || 'Başka bir büro';
            const neden = s.kimlikRedSebebi === 'lisans-yok'
                ? 'için etkin eİcraPro lisansı bulunamadı'
                : 'hesabının eİcraPro lisansında TC kimlik numarası kayıtlı değil';
            return {
                tur: 'bilgi',
                metin: `${buro} ${neden}; bot mevcut hesapla çalışmaya devam ediyor.`,
                gecButonu: false,
                bekliyor: false,
                kapatEtiketi: 'Tamam'
            };
        }
        return null;
    },
    // Socket'e yayınlanan botKimligi yükü. JSON olarak da okunabilir: store.watch yalnız
    // içerik değişince yayın yapsın diye (token yenilemesi yükü değiştirmez).
    botKimligiYuku: (s: AppState) => botKimligiYuku(
        s.aktifKimlik,
        !!s.bekleyenKimlik && !s.bekleyenKimlik.yoksayildi &&
            (s.bekleyenKimlik.sebep === 'tenant' || s.bekleyenKimlik.sebep === 'tckn'),
        avukatAdSoyad(s)
    ),
    botBeklemeBilgisi: (s: AppState) => (
        s.botBeklemeSebebi ? BOT_BEKLEME_METINLERI[s.botBeklemeSebebi] : null
    )
};

/**
 * Plugin'in "son işlenen görev" referansını (GorevYoneticisi.enSonIslenenGorev) siler. Alan
 * plugin modülünün state'inde; plugin ağacı bu repoda salt aynadır, bu yüzden silme kimlik
 * tarafında yapılır. Modül yoksa (testlerde kısmi state) sessizce geçer.
 */
function sonIslenenGorevReferansiniSil(state: AppState): void {
    const gy = (state as unknown as {gorevYoneticisi?: {enSonIslenenGorev?: unknown}}).gorevYoneticisi;
    if (gy) {
        gy.enSonIslenenGorev = null;
    }
}

export const aktifKimlikMutations = {
    aktifKimligiAyarla(state: AppState, kimlik: AktifKimlik) {
        // Büro değişiyorsa hatırlanan "son işlenen görev" ESKİ büronun görevidir: yeni kimlikle
        // onun üst görevine PUT atılırsa DEV-3897 yalıtımı 404 verir ve 'durduruluyor' dalı
        // kilitlenir. Plugin da görevin bürosunu karşılaştırır; bu ek savunmadır. Aynı büroda
        // token yenilemesi referansı SİLMEZ: süren paketin üst görevi hâlâ geçerlidir.
        if (!state.aktifKimlik || state.aktifKimlik.tenant_id !== kimlik.tenant_id) {
            sonIslenenGorevReferansiniSil(state);
        }
        const oncekiKimlikYoktu = !state.aktifKimlik;
        // Yeni token (ilk kimlik, token yenilemesi, büro geçişi) token kurtarma geçmişini sıfırlar: büyüyen
        // aralık eski token'ın 401'lerine aittir. Kurtarma yolunda AYNI token yeniden ayarlanır, geçmiş korunur
        // (kurtarılan token yine 401 verirse aralık büyümeye devam eder).
        if (!state.aktifKimlik || tokenGovdesi(state.aktifKimlik.token) !== tokenGovdesi(kimlik.token)) {
            tokenKurtarmaBasarisizSayisi = 0;
            sonTokenKurtarmasi = 0;
        }
        state.aktifKimlik = kimlik;
        try {
            window.sessionStorage.setItem(AKTIF_KIMLIK_ANAHTARI, JSON.stringify(kimlik));
        } catch (e) {
            // sessionStorage yazılamıyorsa kimlik yalnız bellekte kalır (pencere yenilenince gelen öneri yeniden doğrulanır).
        }
        state.kimlikTokenGecersiz = false;
        state.kimlikRedSebebi = null;
        state.kimlikReddiBuro = null;
        // Kimlik tamamlandı: kimlikten kaynaklanan bekleme bölümü biter. EICRAPRO_BEKLENIYOR hep kimlik
        // kaynaklıdır. TCKN_EKSIK ise iki kaynaktan gelir: lisans reddi (aktif kimlik YOKKEN; kimlik
        // şimdi tamamlandı) ve kartın sertifikasında TCKN okunamaması (aktif kimlik VARKEN, kapı
        // 'kart-tckn-yok'). Aynı büroda token yenilemesi ya da büro geçişi ikincisini silmemeli: sebep
        // kimlikle ilgisiz, dongu sonraki tikte aynı nedeni yeniden koyar ve her token yenilemesinde
        // ikinci bildirim + ses çalardı (bölüm başına BİR bildirim kuralı).
        if (state.botBeklemeSebebi === 'EICRAPRO_BEKLENIYOR' ||
            (state.botBeklemeSebebi === 'TCKN_EKSIK' && oncekiKimlikYoktu)) {
            state.botBeklemeSebebi = null;
        }
    },
    aktifKimligiGeriYukle(state: AppState) {
        let ham: unknown = null;
        try {
            const metin = window.sessionStorage.getItem(AKTIF_KIMLIK_ANAHTARI);
            ham = metin ? JSON.parse(metin) : null;
        } catch (e) {
            ham = null;
        }
        state.aktifKimlik = aktifKimligiDogrula(ham);
    },
    setKimlikTokenGecersiz(state: AppState, deger: boolean) {
        state.kimlikTokenGecersiz = deger;
    },
    setBekleyenKimlik(state: AppState, bekleyen: BekleyenKimlik | null) {
        state.bekleyenKimlik = bekleyen;
    },
    setKimlikReddi(state: AppState, red: {sebep: KimlikRedSebebi, buro: string} | null) {
        state.kimlikRedSebebi = red ? red.sebep : null;
        state.kimlikReddiBuro = red ? red.buro : null;
    },
    setKimlikOneriParmakIzi(state: AppState, izi: string | null) {
        state.kimlikOneriParmakIzi = izi;
    },
    setKimlikSorgusuHatasi(state: AppState, zaman: number | null) {
        state.kimlikSorgusuHatasi = zaman;
    },
    setGecisDurumu(state: AppState, durum: 'bekliyor' | null) {
        state.gecisDurumu = durum;
    },
    setOturumYenilenmeli(state: AppState, deger: boolean) {
        state.oturumYenilenmeli = deger;
    }
};

/**
 * Botun bekleme bölümünü ayarlar. Bölüm başına BİR işletim sistemi bildirimi + ses: sebep
 * aynıysa ya da bölüm içinde değişiyorsa yenisi yok; bölüm biterse (sebep null) sonraki engel
 * yeni bölümdür. Sebep metni özelleştirilebilir (ör. lisans reddi).
 */
export function botBeklemeyiAyarla(
    ctx: Ctx,
    sebep: BotBeklemeSebebi,
    ozel?: {baslik?: string, bildirim?: string}
): void {
    const onceki = ctx.state.botBeklemeSebebi;
    if (onceki === sebep) {
        return;
    }
    ctx.commit('setBotBeklemeSebebi', sebep);
    if (onceki === null) {
        const metin = BOT_BEKLEME_METINLERI[sebep];
        isletimSistemiBildirimi(
            ozel && ozel.baslik ? ozel.baslik : metin.baslik,
            ozel && ozel.bildirim ? ozel.bildirim : metin.bildirim
        );
        uyariSesi();
    }
}

/**
 * Oyuncuyu duraklatır (kimlik uyuşmazlığı ve 'Geç'): playerStop çalışan üst görevleri ESKİ
 * kimlikle DURAKLATILDI yapar ve player'ı 'durduruluyor'a alır; arayüzün Durdur düğmesiyle
 * aynı yoldan 'durduruluyor' socket'e yayınlanır, dongu'nun 'durduruluyor' dalı 'dur'u
 * yayınlar. Döngü hiç kurulmadıysa (taskIntervalHandle yok) 'dur' doğrudan yazılır.
 * Hatalar yutulur ve loglanır: duraklatma başarısız olsa da çağıran akış sürmeli.
 */
export async function oyuncuyuDuraklat(ctx: Ctx): Promise<void> {
    try {
        await ctx.dispatch('playerStop');
    } catch (e) {
        console.error('Görevler duraklatılamadı:', e);
    }
    try {
        app.$socket.client.emit('player', PlayerDurum.durduruluyor);
        const gy: {taskIntervalHandle?: unknown} | undefined = (ctx.state as unknown as {
            gorevYoneticisi?: {taskIntervalHandle?: unknown}
        }).gorevYoneticisi;
        if (!gy || !gy.taskIntervalHandle) {
            ctx.commit('setPlayerDurum', PlayerDurum.dur);
            app.$socket.client.emit('player', PlayerDurum.dur);
        }
    } catch (e) {
        console.error('Player durumu yayınlanamadı:', e);
    }
}

/** Görev listesini boşaltıp sunucudan yeniden çeker (büro değişimi). */
function gorevListesiniSifirla(ctx: Ctx): void {
    ctx.commit('updateGorevler', {completed_tasks: [], incomplete_tasks: [], processed_task_childs: []});
    ctx.dispatch('gorevListesiniTazele').catch((e: unknown) => console.warn('Görev listesi yenilenemedi:', e));
}

/**
 * Aktif kimlik değiştikten (ya da ilk kez oluştuktan) sonraki yan etkiler. UYAP kısmı
 * fire-and-forget: çağıran (updateToken, güvenli nokta) UYAP girişini beklemez. taskInterval
 * BURADA kurulmaz: App.vue onUpdateToken kurar (store testlerinde zamanlayıcı sızmasın).
 */
export function kimlikAktifOlduktanSonra(
    ctx: Ctx,
    secenek: {ilk?: boolean, tcknDegisti?: boolean, buroDegisti?: boolean}
): void {
    const hesapTckn = ctx.getters.hesapTckn;
    // Kart seçimi lisans TCKN'siyle yeniden hesaplanır: seçili kart yeni hesaba ait değilse
    // hesaba ait kart seçilir (belirsizse seçim temizlenir, kullanıcı seçer).
    const kartlar = ctx.state.availableCardList;
    if (Array.isArray(kartlar) && kartlar.length > 0) {
        const secili = kartlar.find((c: any) => c?.certificate?.certificateId === ctx.state.selectedCardSerial);
        if (!secili || tcknNormalize(secili.certificate?.subjectSerial) !== tcknNormalize(hesapTckn)) {
            ctx.commit('setSelectedCardSerial', hesabaAitKartiSec(kartlar, hesapTckn));
        }
    }
    if (secenek.buroDegisti) {
        gorevListesiniSifirla(ctx);
    }
    if (secenek.tcknDegisti) {
        // UYAP oturumu ESKİ avukata ait: bağlı sayılmaz, bir sonraki giriş zorla yapılır
        // (eski oturum "başka hesap" diyaloğuna yol açmasın).
        ctx.commit('setUyapConnection', false);
        ctx.commit('setUyapAvukatBilgileri', undefined);
        ctx.commit('setOturumYenilenmeli', true);
    }
    const uyapAkisi = async () => {
        if ((secenek.ilk || !ctx.getters.isUyapConnected) && !ctx.state.oturumYenilenmeli) {
            await ctx.dispatch('checkUyapSessionNew');
        }
        if (!ctx.getters.isUyapConnected) {
            await ctx.dispatch('otomatikGirisDene', {kartGecisi: secenek.ilk === true || secenek.tcknDegisti === true});
        }
    };
    uyapAkisi().catch((e: unknown) => console.error('Kimlik sonrası UYAP akışı başarısız:', e));
    ctx.dispatch('botKimligiYayinla');
}

// Tek uçuş + sondan birleştirme: çalışırken gelen çağrı bir tur daha ister ve mevcut
// promise'i paylaşır (tur başında öneri yeniden okunur ve parmak izi karşılaştırılır; ucuz).
let oneriUcusu: Promise<void> | null = null;
let oneriTekrar = false;
let oneriTekrarZorla = false;

async function oneriTuru(ctx: Ctx, zorla: boolean): Promise<void> {
    let oneri = oneriyiOku(oturumDeposu());
    if (!oneri && ctx.state.kimlikTokenGecersiz && ctx.state.aktifKimlik) {
        // 401 ham token'ı sildi ve tarayıcı yeniden devretmedi (boşta bir sekmede gece gelen geçici 401):
        // hiçbir imzacı yolu bayrağı kaldıramazdı, bot sabaha kadar beklerdi. Aktif kimliğin KENDİ
        // token'ıyla aynı yoklama yapılır (aşağıdaki tokenKurtarma kolu: zaman sınırlı, 401'de büyüyen
        // aralık); lisans sorgusu başarılı ve kimlik değişmediyse bayrak kalkar.
        const a = ctx.state.aktifKimlik;
        oneri = {token: a.token, tenant: a.tenant, tenant_id: a.tenant_id, tckn: a.tckn};
    }
    if (!oneri) {
        return;
    }
    const izi = oneriParmakIzi(oneri);
    const onceki = ctx.state.aktifKimlik;
    // 401 sonrası 'token geçersiz' iken AYNI token yeniden geldi (geçici/sahte 401 olabilir: Laravel SSO
    // zaman aşımı, tenant eşleşmesi...). Aşağıdaki kısa devreler atlanır ve lisans sorgusu bir kez daha
    // denenir; başarılıysa bayrak kalkar ('degisiklik-yok' dalı). Fırtına olmasın diye en erken
    // KIMLIK_SORGUSU_YENIDEN_DENEME_MS'de bir (zorla/kullanıcı eylemi hariç: beklemeyi sıfırlar).
    const tokenKurtarma = ctx.state.kimlikTokenGecersiz && onceki !== null &&
        tokenGovdesi(onceki.token) === tokenGovdesi(oneri.token) && onceki.tenant_id === oneri.tenant_id;
    if (tokenKurtarma) {
        if (Date.now() < tokenKurtarmaSonrakiDeneme) {
            return;
        }
        // Cevap gelene kadar (ve ağ hatasında) en erken 15 sn sonra; 401/403 ya da başarısız kurtarma bunu büyütür.
        tokenKurtarmaSonrakiDeneme = Date.now() + KIMLIK_SORGUSU_YENIDEN_DENEME_MS;
    } else {
        // Aynı öneri art arda gelirse (updateToken çift gelebilir) UYAP'a da API'ye de sıfır istek.
        if (!zorla && izi === ctx.state.kimlikOneriParmakIzi) {
            return;
        }
        // Pencere yenilemesinde geri yüklenen kimlikle aynı öneri: lisans sorgusu gerekmez.
        if (!zorla && onceki && tokenGovdesi(onceki.token) === tokenGovdesi(oneri.token) &&
            onceki.tenant_id === oneri.tenant_id) {
            ctx.commit('setKimlikOneriParmakIzi', izi);
            ctx.commit('setBekleyenKimlik', null);
            ctx.commit('setKimlikReddi', null);
            return;
        }
    }

    let lisans: LisansKimligi | null;
    try {
        // Öneri token'ı config.kimlik ile verilir: axios aktif kimlik yerine bunu kullanır,
        // X-Imzaci-* başlıkları eklemez, toast atmaz.
        const cevap: unknown = await app.$http.get('/api/v1/imzaci/kimlik', {
            kimlik: {token: oneri.token, tenant_id: oneri.tenant_id},
            sessiz: true,
            timeout: 15000
        });
        lisans = lisansCevabiniCoz(cevap);
    } catch (e) {
        const durum = (e as {response?: {status?: number}} | null | undefined)?.response?.status;
        if (durum === 404 || durum === 405 || durum === 501) {
            // Uç yok (eski backend): yedek yalnız bu durumda; ağ hatası/5xx'te setConfig'in
            // kullanıcı kontrollü TCKN'sine DÜŞÜLMEZ.
            lisans = yedekLisans(oneri);
        } else if (durum === 401 || durum === 403) {
            // Öneri token'ı geçersiz: durum değişmez, aynı öneri tekrar denenmez.
            ctx.commit('setKimlikOneriParmakIzi', izi);
            if (tokenKurtarma) {
                tokenKurtarmaBasarisiz();
            }
            return;
        } else {
            ctx.commit('setKimlikSorgusuHatasi', Date.now());
            return;
        }
    }
    if (lisans === null) {
        // 200 ama tanınmayan gövde: ne reddet ne terfi; sağlık tikinde yeniden denenir.
        ctx.commit('setKimlikSorgusuHatasi', Date.now());
        return;
    }
    ctx.commit('setKimlikSorgusuHatasi', null);

    // Cevap beklenirken 401 / 'Geç' olmuş olabilir: karar GÜNCEL duruma göre verilir.
    const aktif = ctx.state.aktifKimlik;
    const karar = kimlikKarari(aktif, oneri, lisans);
    switch (karar.tur) {
        case 'reddet': {
            const mesaj = karar.sebep === 'lisans-yok' ? EICRAPRO_LISANS_YOK_MESAJI : EICRAPRO_TCKN_EKSIK_MESAJI;
            ctx.commit('setKimlikReddi', {sebep: karar.sebep, buro: oneri.tenant.title});
            if (!aktif) {
                // Başlık da sebebe göre: yalnız gövde özelleşirse lisans yokken "TC kimlik numarası eksik" yazardı.
                botBeklemeyiAyarla(ctx, 'TCKN_EKSIK', {
                    baslik: karar.sebep === 'lisans-yok' ? 'Bot bekliyor: eİcraPro lisansı yok' : undefined,
                    bildirim: mesaj
                });
                ctx.dispatch('botKimligiYayinla');
                // Parmak izi KAYDEDİLMEZ: destek lisanstaki TCKN'yi düzeltince bir sonraki
                // yoklama (updateToken/sağlık tiki) kimliği kabul edebilsin.
                ctx.commit('setKimlikSorgusuHatasi', Date.now());
                return;
            }
            break;
        }
        case 'aktif-ol': {
            const yeni = aktifKimligeDonustur(oneri, lisans);
            if (yeni) {
                ctx.commit('aktifKimligiAyarla', yeni);
                ctx.commit('setBekleyenKimlik', null);
                kimlikAktifOlduktanSonra(ctx, {ilk: true});
            }
            break;
        }
        case 'degisiklik-yok':
            ctx.commit('setBekleyenKimlik', null);
            ctx.commit('setKimlikReddi', null);
            // Token 401'den sonra aynı kaldı ve lisans sorgusu ŞİMDİ başarılı: 401 geçiciymiş, token
            // geçerli. aktifKimligiAyarla bayrağı ve kimlik kaynaklı bekleme bölümünü (EICRAPRO_BEKLENIYOR)
            // kapatır; UYAP akışı ve döngü kapısı yeniden açılır (aktif kimlikteki gibi). Cevap beklenirken
            // bayrak başka yoldan kalktıysa (yeni token) yapılacak bir şey yok.
            if (tokenKurtarma && aktif !== null && ctx.state.kimlikTokenGecersiz) {
                ctx.commit('aktifKimligiAyarla', aktif);
                // Kurtarma aynı token'la yine 401'e dönerse (sorgu geçiyor, iş uçları geçmiyor) sonraki aralık
                // büyür: aksi halde her tikte bekleme bölümü + bildirim + ses yeniden doğardı.
                sonTokenKurtarmasi = Date.now();
                tokenKurtarmaBasarisizSayisi++;
                kimlikAktifOlduktanSonra(ctx, {});
            }
            break;
        default: {
            const yeni = aktifKimligeDonustur(oneri, lisans);
            if (!yeni) {
                break;
            }
            const hemen = hemenUygulanabilirMi(karar, {
                botBosta: ctx.getters.getIslemDurum === IslemDurum.BOSTA,
                kuyrukBos: (ctx.getters.getSiradakiGorevler || []).length === 0,
                tokenGecersiz: ctx.state.kimlikTokenGecersiz
            });
            if (hemen) {
                ctx.commit('aktifKimligiAyarla', yeni);
                ctx.commit('setBekleyenKimlik', null);
                if (karar.tur === 'buro-gec') {
                    kimlikAktifOlduktanSonra(ctx, {buroDegisti: true});
                } else {
                    ctx.dispatch('botKimligiYayinla');
                }
            } else {
                // Tek yuva: en yeni öneri kazanır.
                ctx.commit('setBekleyenKimlik', {
                    kimlik: yeni,
                    sebep: karar.tur === 'token-guncelle' ? 'token' : (karar.tur === 'buro-gec' ? 'tenant' : 'tckn'),
                    zaman: Date.now(),
                    yoksayildi: false
                });
                ctx.dispatch('botKimligiYayinla');
            }
        }
    }
    ctx.commit('setKimlikOneriParmakIzi', izi);
    // Bayrak hâlâ duruyorsa (reddedildi, başka hesaba geçiş önerisi, bekleyen yuva) bu deneme de başarısızdır.
    if (tokenKurtarma && ctx.state.kimlikTokenGecersiz) {
        tokenKurtarmaBasarisiz();
    }
}

export const aktifKimlikActions = {
    /**
     * Tarayıcıdan gelen öneriyi işler (updateToken, sağlık tiki, Bağlan, açılış). Tek uçuş.
     * neden 'saglik': son sorgu hatasından bu yana 15 sn dolmadıysa denemez. zorla: aynı
     * öneriyi de yeniden doğrular (lisans değişmiş olabilir; diyalogdaki 'Yeniden bağla').
     */
    async kimlikOnerisiniIsle(
        ctx: Ctx,
        payload?: {neden?: 'updateToken' | 'saglik' | 'baglan' | 'yukleme' | 'kullanici', zorla?: boolean}
    ): Promise<void> {
        const zorla = payload?.zorla === true;
        // Kullanıcı eylemi (Bağlan, 'Yeniden bağla') token kurtarma beklemesine takılmaz.
        if (zorla || payload?.neden === 'baglan' || payload?.neden === 'kullanici') {
            tokenKurtarmaSonrakiDeneme = 0;
            tokenKurtarmaBasarisizSayisi = 0;
        }
        const hata = ctx.state.kimlikSorgusuHatasi;
        if (payload?.neden === 'saglik' && !zorla && hata !== null &&
            Date.now() - hata < KIMLIK_SORGUSU_YENIDEN_DENEME_MS) {
            return;
        }
        if (oneriUcusu) {
            oneriTekrar = true;
            oneriTekrarZorla = oneriTekrarZorla || zorla;
            return oneriUcusu;
        }
        oneriUcusu = (async () => {
            try {
                await oneriTuru(ctx, zorla);
                while (oneriTekrar) {
                    const tekrarZorla = oneriTekrarZorla;
                    oneriTekrar = false;
                    oneriTekrarZorla = false;
                    await oneriTuru(ctx, tekrarZorla);
                }
            } finally {
                oneriUcusu = null;
                oneriTekrar = false;
                oneriTekrarZorla = false;
            }
        })();
        return oneriUcusu;
    },

    /**
     * GÜVENLİ NOKTA: plugin dongu görev almadan hemen önce çağırır ("şu an görev
     * taşınmıyor" beyanı). islemDurum'a BAKMAZ ve ağ beklemez; asla fırlatmaz. Sebep 'tckn'
     * ASLA uygulanmaz (yalnız kullanıcının 'Geç'i); 'tenant' yalnız kuyruk boşken ya da aktif
     * kimliğin token'ı geçersizken (401: eski büronun görevleri zaten ilerleyemez, kuyruk koruması
     * anlamsız; `hemenUygulanabilirMi` ile aynı kural); 'token' her zaman.
     */
    bekleyenKimligiUygula(ctx: Ctx): {uygulandi: boolean} {
        try {
            const b = ctx.state.bekleyenKimlik;
            if (!b || b.sebep === 'tckn') {
                return {uygulandi: false};
            }
            if (b.sebep === 'tenant' && !ctx.state.kimlikTokenGecersiz &&
                (ctx.getters.getSiradakiGorevler || []).length > 0) {
                return {uygulandi: false};
            }
            const eski = ctx.state.aktifKimlik;
            ctx.commit('aktifKimligiAyarla', b.kimlik);
            ctx.commit('setBekleyenKimlik', null);
            if (!eski || eski.tenant_id !== b.kimlik.tenant_id) {
                kimlikAktifOlduktanSonra(ctx, {buroDegisti: true});
            } else {
                ctx.dispatch('botKimligiYayinla');
            }
            return {uygulandi: true};
        } catch (e) {
            console.error('Bekleyen kimlik uygulanamadı:', e);
            return {uygulandi: false};
        }
    },

    /**
     * Kullanıcı 'Geç'e bastı: süren üst görev ESKİ kimlikle DURAKLATILDI yapılır, player durur,
     * süren alt görev eski kimlikle bitince yeni kimlik aktif olur ve UYAP girişi yeni TCKN ile
     * yapılır. Player 'dur' KALIR (kullanıcı Başlat'a basar).
     */
    async kimlikGecisiniBaslat(ctx: Ctx): Promise<void> {
        const bekleyen = ctx.state.bekleyenKimlik;
        if (!bekleyen || ctx.state.gecisDurumu) {
            return;
        }
        const hedef = bekleyen.kimlik;
        ctx.commit('setGecisDurumu', 'bekliyor');
        try {
            await oyuncuyuDuraklat(ctx);
            // Süren alt görev eski kimlikle bitsin (süre sınırı yok; şerit 'geçiş yapılıyor' gösterir).
            while (ctx.getters.getIslemDurum !== IslemDurum.BOSTA) {
                const guncel = ctx.state.bekleyenKimlik;
                if (!guncel || guncel.kimlik !== hedef) {
                    return; // bekleyen başka öneriyle değişti ya da silindi: geçiş iptal
                }
                await bekle(GECIS_YOKLAMA_MS);
            }
            const guncel = ctx.state.bekleyenKimlik;
            if (!guncel || guncel.kimlik !== hedef) {
                return;
            }
            const eski = ctx.state.aktifKimlik;
            ctx.commit('aktifKimligiAyarla', hedef);
            ctx.commit('setBekleyenKimlik', null);
            ctx.commit('setKimlikUyusmazligiUyarisi', null);
            ctx.commit('setKimlikDuraklatmasi', null);
            kimlikAktifOlduktanSonra(ctx, {
                tcknDegisti: !eski || eski.tckn !== hedef.tckn,
                buroDegisti: !eski || eski.tenant_id !== hedef.tenant_id
            });
        } finally {
            ctx.commit('setGecisDurumu', null);
        }
    },

    /** Şeridi gizler ('Yoksay' / 'Tamam'); aynı öneri şerit olarak geri gelmez. */
    kimlikGecisiniYoksay(ctx: Ctx): void {
        const b = ctx.state.bekleyenKimlik;
        if (b && !b.yoksayildi) {
            ctx.commit('setBekleyenKimlik', {...b, yoksayildi: true});
        } else if (ctx.state.kimlikReddiBuro !== null) {
            ctx.commit('setKimlikReddi', null);
        }
    },

    /**
     * API 401 verdi (axios yanıt interceptor'u çağırır). Eski token'la giden geç bir istek
     * yok sayılır. Bekleyen 'token' önerisi varsa HEMEN terfi eder (aynı kimlik, taze token;
     * bot durmaz); yoksa kimlik token'ı geçersiz sayılır: dongu ve UYAP girişi yeni öneri
     * gelene kadar bekler. aktifKimlik SİLİNMEZ (hesapTckn kaynağı dolu kalır). Geçici/sahte bir
     * 401 botu kalıcı durdurmasın diye lisans sorgusu bir kez daha denenir (oneriTuru): aynı token
     * tarayıcıdan yeniden gelirse (updateToken, sağlık tiki, Bağlan) onunla, gelmezse (ham token silindi)
     * sağlık tikinde aktif kimliğin kendi token'ıyla; başarılıysa ve kimlik değişmediyse bayrak kalkar.
     */
    kimlikTokeniGecersizBildir(ctx: Ctx, payload?: {kullanilanToken?: string | null}): void {
        const aktif = ctx.state.aktifKimlik;
        const kullanilan = payload?.kullanilanToken;
        if (!aktif || typeof kullanilan !== 'string' || tokenGovdesi(kullanilan) !== tokenGovdesi(aktif.token)) {
            return;
        }
        const b = ctx.state.bekleyenKimlik;
        if (b && b.sebep === 'token') {
            ctx.commit('aktifKimligiAyarla', b.kimlik);
            ctx.commit('setBekleyenKimlik', null);
            return;
        }
        if (ctx.state.kimlikTokenGecersiz) {
            return;
        }
        ctx.commit('setKimlikTokenGecersiz', true);
        // Lisans sorgusu (aynı token tarayıcıdan geldiğinde ya da aktif token'la sağlık tikinden) en erken bu
        // süre sonra denenir (anında değil). Son kurtarma yakınsa (kurtarılan token yine 401) büyüme sürer.
        if (Date.now() - sonTokenKurtarmasi > TOKEN_KURTARMA_KARARLI_MS) {
            tokenKurtarmaBasarisizSayisi = 0;
        }
        tokenKurtarmaSonrakiDeneme = Date.now() + tokenKurtarmaAraligi();
        if (ctx.getters.getPlayerDurum == PlayerDurum.oto) {
            botBeklemeyiAyarla(ctx, 'EICRAPRO_BEKLENIYOR');
        }
        // Tarayıcı bu arada daha yeni bir token yazmış olabilir: öneri yeniden değerlendirilir.
        ctx.dispatch('kimlikOnerisiniIsle', {neden: 'saglik'}).catch(() => undefined);
    },

    /** Socket'e botKimligi yayınlar (TCKN içermez; aktif kimlik yoksa tenant_id ''). */
    botKimligiYayinla(ctx: Ctx): void {
        try {
            app.$socket.client.emit('botKimligi', ctx.getters.botKimligiYuku);
        } catch (e) {
            console.warn('botKimligi yayınlanamadı:', e);
        }
    }
};
